import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  BanknotesIcon,
  BuildingLibraryIcon,
  Cog6ToothIcon,
  DevicePhoneMobileIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import {
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  FiltersTabs,
  IconButton,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../components/ui';

interface ApiRequisite {
  id: number;
  details?: string;
  priority: number;
  label?: string;
  is_active?: boolean;
  isActive?: boolean;
  created_at?: string;
  updated_at?: string;
  device_name?: string;
  deviceName?: string;
  card_number?: string;
  cardNumber?: string;
  phone_number?: string;
  phoneNumber?: string;
  recipient_name?: string;
  recipientName?: string;
}

type RequisiteType = 'card' | 'phone' | 'account' | 'custom';

type NormalizedRequisite = {
  id: number;
  label: string;
  priority: number;
  active: boolean;
  type: RequisiteType;
  cardNumber?: string;
  phoneNumber?: string;
  accountNumber?: string;
  bankName?: string;
  recipientName?: string;
  deviceName?: string;
  rawDetails: string;
  createdAt?: string;
  updatedAt?: string;
};

type FormState = {
  type: RequisiteType;
  label: string;
  priority: number;
  isActive: boolean;
  deviceName: string;
  cardNumber: string;
  phoneNumber: string;
  recipientName: string;
  bankName: string;
  customDetails: string;
};

type FilterKey = 'all' | 'active' | 'inactive' | 'card' | 'phone' | 'account' | 'custom';

const defaultFormState: FormState = {
  type: 'card',
  label: '',
  priority: 5,
  isActive: true,
  deviceName: '',
  cardNumber: '',
  phoneNumber: '',
  recipientName: '',
  bankName: '',
  customDetails: '',
};

const typeMeta: Record<RequisiteType, { label: string; icon: React.ReactNode; className: string }> = {
  card: {
    label: 'Банковская карта',
    icon: <BanknotesIcon className="h-4 w-4" />,
    className: 'bg-primary-50 text-primary-600',
  },
  phone: {
    label: 'Номер телефона',
    icon: <DevicePhoneMobileIcon className="h-4 w-4" />,
    className: 'bg-warning/10 text-warning',
  },
  account: {
    label: 'Банковский счёт',
    icon: <BuildingLibraryIcon className="h-4 w-4" />,
    className: 'bg-success/10 text-success',
  },
  custom: {
    label: 'Произвольный формат',
    icon: <Cog6ToothIcon className="h-4 w-4" />,
    className: 'bg-secondary-100 text-secondary-600',
  },
};

const parseDetails = (details?: string) => {
  if (!details) return {};
  try {
    return JSON.parse(details);
  } catch {
    return { raw: details };
  }
};

const detectType = (api: ApiRequisite, parsed: any): RequisiteType => {
  const card = api.card_number || api.cardNumber || parsed?.number || parsed?.card;
  const phone = api.phone_number || api.phoneNumber || parsed?.phone;
  const account = parsed?.account || parsed?.iban;
  if (parsed?.type && ['card', 'phone', 'account', 'custom'].includes(parsed.type)) {
    return parsed.type;
  }
  if (card) return 'card';
  if (phone) return 'phone';
  if (account) return 'account';
  return 'custom';
};

const normalizeRequisite = (apiReq: ApiRequisite): NormalizedRequisite => {
  const parsed = parseDetails(apiReq.details);
  const type = detectType(apiReq, parsed);
  const cardNumber = apiReq.card_number || apiReq.cardNumber || parsed?.number || parsed?.card || '';
  const phoneNumber = apiReq.phone_number || apiReq.phoneNumber || parsed?.phone || '';
  const accountNumber = parsed?.account || parsed?.iban || '';
  const bankName = parsed?.bank || '';
  const deviceName = apiReq.device_name || apiReq.deviceName || parsed?.device || '';
  const recipientName = apiReq.recipient_name || apiReq.recipientName || parsed?.holder || parsed?.recipient || '';

  return {
    id: apiReq.id,
    label: apiReq.label || parsed?.label || `Реквизит #${apiReq.id}`,
    priority: apiReq.priority,
    active: apiReq.is_active ?? apiReq.isActive ?? true,
    type,
    cardNumber,
    phoneNumber,
    accountNumber,
    bankName,
    deviceName,
    recipientName,
    rawDetails: apiReq.details || '',
    createdAt: apiReq.created_at,
    updatedAt: apiReq.updated_at,
  };
};

const formatCard = (value?: string) => {
  if (!value) return '—';
  return value.replace(/\s+/g, '').replace(/(\d{4})(?=\d)/g, '$1 ').trim();
};

const formatPhone = (value?: string) => {
  if (!value) return '—';
  return value.replace(/(\d{1,3})(\d{3})(\d{2})(\d{2})/, '+$1 $2-$3-$4');
};

const formatAccount = (value?: string) => {
  if (!value) return '—';
  return value.replace(/\s+/g, '').replace(/(.{4})(?=.)/g, '$1 ').trim();
};

const formatDate = (value?: string) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('ru-RU');
};

const buildDetailsSummary = (req: NormalizedRequisite) => {
  switch (req.type) {
    case 'card':
      return `${formatCard(req.cardNumber)}${req.bankName ? ` • ${req.bankName}` : ''}`;
    case 'phone':
      return formatPhone(req.phoneNumber);
    case 'account':
      return `${formatAccount(req.accountNumber)}${req.bankName ? ` • ${req.bankName}` : ''}`;
    case 'custom':
    default:
      return req.rawDetails ? `${req.rawDetails.slice(0, 42)}${req.rawDetails.length > 42 ? '…' : ''}` : '—';
  }
};

const buildDetailsMeta = (req: NormalizedRequisite) => {
  switch (req.type) {
    case 'card':
      return [
        { label: 'Номер', value: formatCard(req.cardNumber) },
        { label: 'Банк', value: req.bankName || '—' },
        { label: 'Держатель', value: req.recipientName || '—' },
      ];
    case 'phone':
      return [
        { label: 'Телефон', value: formatPhone(req.phoneNumber) },
        { label: 'Получатель', value: req.recipientName || '—' },
      ];
    case 'account':
      return [
        { label: 'Счёт', value: formatAccount(req.accountNumber) },
        { label: 'Банк', value: req.bankName || '—' },
        { label: 'Получатель', value: req.recipientName || '—' },
      ];
    case 'custom':
    default:
      return [
        { label: 'Детали', value: req.rawDetails || '—' },
      ];
  }
};

const getFilterLabel = (key: FilterKey) => {
  switch (key) {
    case 'all':
      return 'Все';
    case 'active':
      return 'Активные';
    case 'inactive':
      return 'Неактивные';
    case 'card':
      return 'Карты';
    case 'phone':
      return 'Телефоны';
    case 'account':
      return 'Счета';
    case 'custom':
      return 'Произвольные';
    default:
      return key;
  }
};

const Requisites: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canManage = user?.role === 'admin' || user?.role === 'owner';
  const [filter, setFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formState, setFormState] = useState<FormState>(defaultFormState);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const requisitesQuery = useQuery<ApiRequisite[]>({
    queryKey: ['requisites'],
    queryFn: async () => {
      const { data } = await api.get('/admin/requisites');
      return (data?.requisites || []) as ApiRequisite[];
    },
    staleTime: 60_000,
  });

  const requisites = useMemo(
    () => (requisitesQuery.data || []).map(normalizeRequisite),
    [requisitesQuery.data]
  );

  const filteredRequisites = useMemo(() => {
    const term = search.trim().toLowerCase();
    return requisites.filter((req) => {
      const matchesFilter =
        filter === 'all' ||
        (filter === 'active' && req.active) ||
        (filter === 'inactive' && !req.active) ||
        (filter === req.type);

      if (!matchesFilter) return false;

      if (!term) return true;

      return (
        req.label.toLowerCase().includes(term) ||
        (req.recipientName || '').toLowerCase().includes(term) ||
        (req.cardNumber || '').replace(/\s+/g, '').includes(term) ||
        (req.phoneNumber || '').includes(term) ||
        (req.accountNumber || '').includes(term)
      );
    });
  }, [requisites, filter, search]);

  const filterTabs = useMemo(() => {
    const counts: Record<FilterKey, number> = {
      all: 0,
      active: 0,
      inactive: 0,
      card: 0,
      phone: 0,
      account: 0,
      custom: 0,
    };

    requisites.forEach((req) => {
      counts.all += 1;
      if (req.active) counts.active += 1;
      else counts.inactive += 1;
      counts[req.type] += 1;
    });

    return (Object.keys(counts) as FilterKey[]).map((key) => ({
      key,
      label: getFilterLabel(key),
      badge: counts[key],
    }));
  }, [requisites]);

  const openCreateForm = () => {
    setEditingId(null);
    setFormState(defaultFormState);
    setFormError(null);
    setShowForm(true);
  };

  const openEditForm = (req: NormalizedRequisite) => {
    const parsed = parseDetails(req.rawDetails);
    setEditingId(req.id);
    setFormState({
      type: req.type,
      label: req.label,
      priority: req.priority,
      isActive: req.active,
      deviceName: req.deviceName || parsed?.device || '',
      cardNumber: req.cardNumber || req.accountNumber || parsed?.number || parsed?.account || '',
      phoneNumber: req.phoneNumber || parsed?.phone || '',
      recipientName: req.recipientName || parsed?.holder || parsed?.recipient || '',
      bankName: req.bankName || parsed?.bank || '',
      customDetails: req.type === 'custom' ? req.rawDetails : '',
    });
    setFormError(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormState(defaultFormState);
    setFormError(null);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        priority: formState.priority,
        label: formState.label || null,
        isActive: formState.isActive,
        deviceName: formState.deviceName || null,
      };

      if (formState.type === 'card' || formState.type === 'account') {
        payload.cardNumber = formState.cardNumber || null;
      }
      if (formState.type === 'phone') {
        payload.phoneNumber = formState.phoneNumber || null;
      }
      payload.recipientName = formState.recipientName || null;

      if (formState.type === 'custom') {
        payload.details = formState.customDetails || '';
      } else {
        const details: Record<string, unknown> = {
          type: formState.type,
        };

        if (formState.type === 'card') {
          details.number = formState.cardNumber;
          details.bank = formState.bankName;
          details.holder = formState.recipientName;
        }
        if (formState.type === 'phone') {
          details.phone = formState.phoneNumber;
          details.recipient = formState.recipientName;
        }
        if (formState.type === 'account') {
          details.account = formState.cardNumber;
          details.bank = formState.bankName;
          details.holder = formState.recipientName;
        }
        if (formState.deviceName) {
          details.device = formState.deviceName;
        }
        payload.details = JSON.stringify(details);
      }

      if (editingId) {
        await api.put(`/admin/requisites/${editingId}`, payload);
      } else {
        await api.post('/admin/requisites', payload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requisites'] });
      closeForm();
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || 'Не удалось сохранить реквизит';
      setFormError(message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async ({ id, force }: { id: number; force?: boolean }) => {
      const suffix = force ? '?force=1' : '';
      await api.delete(`/admin/requisites/${id}${suffix}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requisites'] });
    },
    onError: async (error: any, variables, context) => {
      const code = error?.response?.data?.code;
      const usage = error?.response?.data?.usageCount;
      if (code === 'REQUISITE_IN_USE') {
        const confirmed = window.confirm(
          `Реквизит используется в ${usage || 0} покупках. Удалить принудительно?`
        );
        if (confirmed) {
          deleteMutation.mutate({ id: variables.id, force: true });
        }
        return;
      }
      const message = error?.response?.data?.error || error?.message || 'Не удалось удалить реквизит';
      alert(message);
    },
  });

  const handleDelete = (req: NormalizedRequisite) => {
    if (!window.confirm('Удалить реквизит?')) return;
    deleteMutation.mutate({ id: req.id });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    saveMutation.mutate();
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-slate-900">Реквизиты для платежей</h1>
          <p className="text-sm text-slate-500">
            Управляйте реквизитами для приема платежей. Добавляйте карты, счёта или телефонные номера.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <IconButton aria-label="Обновить" variant="ghost" onClick={() => requisitesQuery.refetch()}>
            <ArrowPathIcon className="h-5 w-5" />
          </IconButton>
          {canManage ? (
            <button
              onClick={openCreateForm}
              className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-600"
            >
              <PlusIcon className="h-4 w-4" /> Добавить реквизит
            </button>
          ) : null}
        </div>
      </div>

      <Card>
        <CardHeader
          className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between"
          title={<span className="text-lg font-semibold text-slate-900">Список реквизитов</span>}
          description="Фильтруйте по статусу и типу, чтобы быстрее находить нужную запись."
          actions={<FiltersTabs tabs={filterTabs} value={filter} onChange={(key) => setFilter(key as FilterKey)} />}
        />
        <CardBody className="space-y-4">
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm">
            <AdjustmentsHorizontalIcon className="h-5 w-5 text-slate-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Поиск по названию, карте, телефону"
              className="w-full border-none bg-transparent text-sm outline-none placeholder:text-slate-400"
            />
          </div>

          <div className="grid gap-3 md:hidden">
            {requisitesQuery.isLoading ? (
              <div className="rounded-2xl border border-slate-200 px-4 py-6 text-center text-slate-500">
                Загружаем реквизиты...
              </div>
            ) : filteredRequisites.length === 0 ? (
              <EmptyState
                title="Реквизиты не найдены"
                description="Попробуйте изменить фильтры или создать новый реквизит."
              />
            ) : (
              filteredRequisites.map((req) => (
                <div key={req.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${typeMeta[req.type].className}`}>
                          {typeMeta[req.type].icon}
                          {typeMeta[req.type].label}
                        </span>
                        <StatusBadge tone={req.active ? 'success' : 'default'}>
                          {req.active ? 'Активен' : 'Неактивен'}
                        </StatusBadge>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-slate-900">{req.label}</p>
                        <p className="text-sm text-slate-600">{buildDetailsSummary(req)}</p>
                      </div>
                    </div>
                    {canManage ? (
                      <div className="flex items-center gap-2">
                        <IconButton aria-label="Редактировать" variant="ghost" onClick={() => openEditForm(req)}>
                          <PencilSquareIcon className="h-5 w-5" />
                        </IconButton>
                        <IconButton aria-label="Удалить" variant="ghost" onClick={() => handleDelete(req)}>
                          <TrashIcon className="h-5 w-5 text-danger" />
                        </IconButton>
                      </div>
                    ) : null}
                  </div>
                  <div className="mt-3 grid gap-2 text-xs text-slate-500">
                    {buildDetailsMeta(req).map((item) => (
                      <div key={item.label} className="flex justify-between gap-3">
                        <span className="font-medium text-slate-600">{item.label}</span>
                        <span className="text-right text-slate-500">{item.value}</span>
                      </div>
                    ))}
                    <div className="flex justify-between gap-3">
                      <span className="font-medium text-slate-600">Приоритет</span>
                      <span>{req.priority}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="font-medium text-slate-600">Создан</span>
                      <span>{formatDate(req.createdAt)}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="hidden md:block">
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Реквизит</TableHeaderCell>
                    <TableHeaderCell>Детали</TableHeaderCell>
                    <TableHeaderCell>Статус</TableHeaderCell>
                    <TableHeaderCell>Приоритет</TableHeaderCell>
                    <TableHeaderCell>Создан</TableHeaderCell>
                    {canManage ? <TableHeaderCell numeric>Действия</TableHeaderCell> : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requisitesQuery.isLoading ? (
                    <TableRow>
                      <TableCell colSpan={canManage ? 6 : 5} className="text-center text-slate-500">
                        Загружаем реквизиты...
                      </TableCell>
                    </TableRow>
                  ) : filteredRequisites.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canManage ? 6 : 5}>
                        <EmptyState
                          title="Реквизиты не найдены"
                          description="Попробуйте изменить фильтр или создайте новый реквизит."
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRequisites.map((req) => (
                      <TableRow key={req.id}>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${typeMeta[req.type].className}`}>
                                {typeMeta[req.type].icon}
                                {typeMeta[req.type].label}
                              </span>
                              <span className="text-sm font-semibold text-slate-900">{req.label}</span>
                            </div>
                            {req.recipientName ? (
                              <p className="text-xs text-slate-500">Получатель: {req.recipientName}</p>
                            ) : null}
                            {req.deviceName ? (
                              <p className="text-xs text-slate-500">Устройство: {req.deviceName}</p>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1 text-sm text-slate-600">
                            <p>{buildDetailsSummary(req)}</p>
                            {req.bankName ? (
                              <p className="text-xs text-slate-500">Банк: {req.bankName}</p>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell>
                          <StatusBadge tone={req.active ? 'success' : 'default'}>
                            {req.active ? 'Активен' : 'Неактивен'}
                          </StatusBadge>
                        </TableCell>
                        <TableCell>{req.priority}</TableCell>
                        <TableCell>
                          <div className="text-sm text-slate-600">{formatDate(req.createdAt)}</div>
                          {req.updatedAt ? (
                            <div className="text-xs text-slate-400">Обновлён: {formatDate(req.updatedAt)}</div>
                          ) : null}
                        </TableCell>
                        {canManage ? (
                          <TableCell numeric>
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => openEditForm(req)}
                                className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 transition hover:border-primary-200 hover:text-primary-600"
                              >
                                <PencilSquareIcon className="h-4 w-4" /> Редактировать
                              </button>
                              <button
                                onClick={() => handleDelete(req)}
                                className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-danger transition hover:border-danger/30"
                              >
                                <TrashIcon className="h-4 w-4" /> Удалить
                              </button>
                            </div>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </CardBody>
      </Card>

      {showForm ? (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-slate-900/40" onClick={closeForm} />
          <div className="relative flex h-full w-full max-w-xl flex-col bg-white shadow-lifted">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">
                  {editingId ? 'Редактирование' : 'Новый реквизит'}
                </p>
                <h2 className="text-lg font-semibold text-slate-900">
                  {editingId ? 'Обновить реквизит' : 'Создать реквизит'}
                </h2>
              </div>
              <IconButton aria-label="Закрыть" onClick={closeForm}>
                <XMarkIcon className="h-5 w-5" />
              </IconButton>
            </div>
            <form className="flex-1 space-y-5 overflow-y-auto px-5 py-6" onSubmit={handleSubmit}>
              <div className="grid gap-3 sm:grid-cols-2">
                {(Object.keys(typeMeta) as RequisiteType[]).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setFormState((prev) => ({ ...prev, type }))}
                    className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition ${
                      formState.type === type
                        ? 'border-primary-300 bg-primary-50 text-primary-600'
                        : 'border-slate-200 text-slate-600 hover:border-primary-200'
                    }`}
                  >
                    {typeMeta[type].icon}
                    {typeMeta[type].label}
                  </button>
                ))}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="space-y-1 text-sm text-slate-600">
                  <span className="font-medium">Название / пометка</span>
                  <input
                    type="text"
                    value={formState.label}
                    onChange={(event) => setFormState((prev) => ({ ...prev, label: event.target.value }))}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    placeholder="Основная карта"
                  />
                </label>
                <label className="space-y-1 text-sm text-slate-600">
                  <span className="font-medium">Приоритет</span>
                  <input
                    type="number"
                    min={1}
                    value={formState.priority}
                    onChange={(event) =>
                      setFormState((prev) => ({ ...prev, priority: Number(event.target.value) || 1 }))
                    }
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  />
                </label>
              </div>

              <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
                <input
                  type="checkbox"
                  checked={formState.isActive}
                  onChange={(event) => setFormState((prev) => ({ ...prev, isActive: event.target.checked }))}
                  className="h-4 w-4 rounded border-slate-300 text-primary-500 focus:ring-primary-200"
                />
                Реквизит активен и доступен для использования
              </label>

              {formState.type === 'card' || formState.type === 'account' ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="space-y-1 text-sm text-slate-600">
                    <span className="font-medium">{formState.type === 'card' ? 'Номер карты' : 'Номер счёта'}</span>
                    <input
                      type="text"
                      value={formState.cardNumber}
                      onChange={(event) => setFormState((prev) => ({ ...prev, cardNumber: event.target.value }))}
                      required
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                      placeholder={formState.type === 'card' ? '1234 5678 9012 3456' : 'KZ12 3456 7890 1234 5678'}
                    />
                  </label>
                  <label className="space-y-1 text-sm text-slate-600">
                    <span className="font-medium">Название банка</span>
                    <input
                      type="text"
                      value={formState.bankName}
                      onChange={(event) => setFormState((prev) => ({ ...prev, bankName: event.target.value }))}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                      placeholder="Kaspi Bank"
                    />
                  </label>
                </div>
              ) : null}

              {formState.type === 'phone' ? (
                <label className="space-y-1 text-sm text-slate-600">
                  <span className="font-medium">Номер телефона</span>
                  <input
                    type="text"
                    value={formState.phoneNumber}
                    onChange={(event) => setFormState((prev) => ({ ...prev, phoneNumber: event.target.value }))}
                    required
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    placeholder="77001234567"
                  />
                </label>
              ) : null}

              {formState.type !== 'custom' ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="space-y-1 text-sm text-slate-600">
                    <span className="font-medium">Имя получателя</span>
                    <input
                      type="text"
                      value={formState.recipientName}
                      onChange={(event) =>
                        setFormState((prev) => ({ ...prev, recipientName: event.target.value }))
                      }
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                      placeholder="IVANOV IVAN"
                    />
                  </label>
                  <label className="space-y-1 text-sm text-slate-600">
                    <span className="font-medium">Название устройства / заметка</span>
                    <input
                      type="text"
                      value={formState.deviceName}
                      onChange={(event) => setFormState((prev) => ({ ...prev, deviceName: event.target.value }))}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                      placeholder="Terminal #2"
                    />
                  </label>
                </div>
              ) : null}

              {formState.type === 'custom' ? (
                <label className="space-y-1 text-sm text-slate-600">
                  <span className="font-medium">JSON-детали реквизита</span>
                  <textarea
                    value={formState.customDetails}
                    onChange={(event) => setFormState((prev) => ({ ...prev, customDetails: event.target.value }))}
                    rows={6}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    placeholder='{"type":"custom","text":"Реквизиты"}'
                  />
                </label>
              ) : null}

              {formError ? (
                <div className="rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger">{formError}</div>
              ) : null}

              <div className="flex items-center justify-end gap-2 border-t border-slate-200 pt-4">
                <button
                  type="button"
                  onClick={closeForm}
                  className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-primary-200"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={saveMutation.isPending}
                  className="rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saveMutation.isPending ? 'Сохранение...' : editingId ? 'Сохранить изменения' : 'Создать реквизит'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Requisites;
